/**
 * **La plaque de rue partageable** — SPEC-UI §3.7, PLAN §4.13.
 *
 * C'est la récompense finale du jeu : atteindre le million donne une rue à son nom, et cette
 * image est ce qu'on montre. Pour un projet filmé pour CreatorDevIA, c'est aussi *le seul asset
 * viral* qu'il produit ⟦AUDIT-COMPLETUDE I-6⟧ — une capture d'écran d'un tableau de bord ne se
 * poste pas, une plaque émaillée avec son nom dessus, si.
 *
 * **1200 × 630**, la taille exacte que Twitter/X, Facebook, LinkedIn et Discord attendent d'une
 * carte de partage. Ce n'est pas un chiffre d'esthète : à un autre ratio, la plateforme recadre
 * elle-même et coupe le nom.
 *
 * ## Deux règles que ce composant s'impose
 *
 * 1. **Il n'invente jamais un chiffre.** Le score sur 1 000 est calculé côté serveur (§7.4) et
 *    vaut `null` en bac à sable ; le rang vient du classement (§5.8) et n'existe pas tant que la
 *    run n'y est pas. Une ligne dont la donnée manque **n'est pas dessinée** — elle n'est pas
 *    dessinée à zéro, et surtout pas remplacée par une estimation. Une plaque qui annonce
 *    « 0/1000 » à quelqu'un qui vient de gagner est pire que pas de plaque du tout.
 * 2. **Les couleurs viennent de `tokens.css`, pas d'ici.** `--plaque`, `--papier`, `--marqueur`
 *    sont lues au rendu sur `:root`. Un canvas ne cascade pas : sans cette lecture, la plaque
 *    dériverait de la charte au premier ajustement de token, en silence et sans que rien ne
 *    casse. C'est la même raison qui interdit d'écrire une règle de contenu en dur.
 *
 * ## Le téléchargement
 *
 * `canvas.toBlob` + `<a download>` + `URL.createObjectURL`. Le geste est vérifié avant d'être
 * proposé (`"download" in HTMLAnchorElement.prototype`) : l'app est une SPA servie par Fastify
 * en contexte de premier niveau, donc il fonctionne — mais un contexte plus contraint
 * (webview intégrée, iframe sandboxée) le neutraliserait **sans erreur**, et un bouton qui ne
 * fait rien est exactement le défaut que ce projet traque. Quand il n'est pas disponible, on ne
 * montre pas un bouton mort : on dit à quoi se rabattre, et l'image est de toute façon à
 * l'écran, prête pour un clic droit.
 *
 * ⚠️ Aucun `window.alert` / `confirm` / `prompt` (CLAUDE.md) : les retours passent par un toast.
 *
 * ## ⟦C48 · A-168 §3⟧ Redessinée dans la DA du jeu
 *
 * La plaque d'origine était au « marqueur » : papier uni, cadre au trait doublé, lettres en police
 * cursive, plaque bleue penchée de 1,5° sur une ombre floue. C'était la seule image du jeu qui SORT
 * de l'écran, donc celle qu'on voit le plus loin — et la seule à ne plus ressembler à la chambre.
 * Elle est maintenant dessinée comme une fenêtre pixel : fond tramé (le corps des `Fenetre`),
 * bandeau violet, plaque bleue en escalier sur une ombre dure, police VT323, aucune rotation ni
 * anticrénelage (tout est posé sur des entiers). Même contrat de données, mêmes tailles, mêmes
 * lignes conditionnelles : rien n'est inventé, rien n'est déplacé.
 */

import { useEffect, useRef, useState } from "react";

import { useUIStore } from "../stores/uiStore";

/** La taille de partage, en dur : c'est un contrat avec les plateformes, pas un réglage. */
export const PLAQUE_L = 1200;
export const PLAQUE_H = 630;

export interface DonneesPlaque {
  nomChaine: string;
  abonnes: number;
  /** Jours simulés au moment gravé. */
  jours: number;
  videos: number;
  /** Score sur 1 000 — `null` en bac à sable (le serveur seul le calcule). */
  score: number | null;
  /** Rang au classement — `null` tant que la run n'y figure pas. */
  rang: number | null;
  /** Le titre de la fin obtenue (« La Rue », « CEO »…) — `null` si le moteur ne l'a pas tranchée. */
  finTitre: string | null;
  hardcore: boolean;
  /** ⟦PLAN §4.13⟧ Mort après le million : la victoire reste gravée, la plaque le dit. */
  legende?: boolean;
}

/** Une couleur de `tokens.css`, avec un repli si la variable disparaissait. */
function token(nom: string, repli: string): string {
  if (typeof window === "undefined") return repli;
  const v = getComputedStyle(document.documentElement).getPropertyValue(nom).trim();
  return v.length > 0 ? v : repli;
}

/** La police pixel du jeu (`app.css`, `@font-face` local) — c'est elle que la plaque dessine. */
const POLICE_PIXEL = '"VT323", "Press Start 2P", monospace';

/** Le nom du joueur peut être long : on rétrécit jusqu'à ce qu'il tienne, on ne coupe jamais. */
function taillePourTenir(
  ctx: CanvasRenderingContext2D,
  texte: string,
  police: string,
  depart: number,
  largeurMax: number,
  minimum = 40,
): number {
  let taille = depart;
  while (taille > minimum) {
    ctx.font = `${taille}px ${police}`;
    if (ctx.measureText(texte).width <= largeurMax) break;
    taille -= 4;
  }
  return taille;
}

function nombreFr(n: number): string {
  return n.toLocaleString("fr-FR");
}

/**
 * Le rythme vertical du bas de plaque. Nommé plutôt qu'écrit en chiffres dans le dessin :
 * le bandeau kraft et la signature se chevauchaient de quatre pixels — invisible en relecture,
 * flagrant à l'écran, et impossible à voir depuis le code tant que les deux `y` étaient posés
 * à cent lignes d'écart. On les met côte à côte, la collision devient de l'arithmétique.
 */
const BANDEAU_Y = 512;
const BANDEAU_H = 58;
/** Sous le bandeau (570), au-dessus du cadre extérieur (622). */
const PIED_Y = 606;

/**
 * Un rectangle à coins en ESCALIER (deux marches de `c` px) — la forme des bordures de `Fenetre`
 * (`app.css`). Cinq bandes, aucune diagonale, aucun anticrénelage.
 */
function boiteEscalier(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  l: number,
  h: number,
  c: number,
  couleur: string,
): void {
  ctx.fillStyle = couleur;
  ctx.fillRect(x + 2 * c, y, l - 4 * c, c);
  ctx.fillRect(x + c, y + c, l - 2 * c, c);
  ctx.fillRect(x, y + 2 * c, l, h - 4 * c);
  ctx.fillRect(x + c, y + h - 2 * c, l - 2 * c, c);
  ctx.fillRect(x + 2 * c, y + h - c, l - 4 * c, c);
}

/** Le texte, espacé lettre à lettre : `letterSpacing` n'est pas supporté partout sur un contexte 2D. */
function texteEspace(ctx: CanvasRenderingContext2D, texte: string, xCentre: number, y: number, espace: number): void {
  const lettres = [...texte];
  const largeurs = lettres.map((l) => ctx.measureText(l).width);
  const total = largeurs.reduce((t, w) => t + w, 0) + espace * (lettres.length - 1);
  let x = xCentre - total / 2;
  ctx.textAlign = "left";
  lettres.forEach((l, i) => {
    ctx.fillText(l, Math.round(x), y);
    x += (largeurs[i] ?? 0) + espace;
  });
}

/**
 * Dessine la plaque. Fonction **pure du couple (contexte, données)** : aucun état React, aucune
 * lecture de store — c'est ce qui permet de la rappeler à l'identique pour l'export.
 */
function dessiner(ctx: CanvasRenderingContext2D, d: DonneesPlaque): void {
  const liseré = token("--liseré", "#150f0a");
  const creme = token("--creme", "#f3d998");
  const cremeOmbre = token("--creme-ombre", "#e0bd6c");
  const violet = token("--principal", "#6a3fa0");
  const violetOmbre = token("--principal-ombre", "#452869");
  const bleu = token("--plaque", "#1d4e89");
  const crayon = token("--crayon", "#5a4a36");
  const kraft = token("--kraft", "#c9a877");
  const clair = token("--liseré-clair", "#fdfbf3");

  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, PLAQUE_L, PLAQUE_H);

  // ── Le fond : la trame du corps des fenêtres (damier de 12 px), puis le cadre plein ──
  const T = 12;
  for (let y = 0; y < PLAQUE_H; y += T) {
    for (let x = 0; x < PLAQUE_L; x += T) {
      ctx.fillStyle = (x / T + y / T) % 2 === 0 ? creme : cremeOmbre;
      ctx.fillRect(x, y, T, T);
    }
  }
  ctx.fillStyle = liseré;
  ctx.fillRect(0, 0, PLAQUE_L, 10);
  ctx.fillRect(0, PLAQUE_H - 10, PLAQUE_L, 10);
  ctx.fillRect(0, 0, 10, PLAQUE_H);
  ctx.fillRect(PLAQUE_L - 10, 0, 10, PLAQUE_H);

  // ── Le bandeau violet : titre du jeu à gauche, le village à droite ──
  ctx.fillStyle = violet;
  ctx.fillRect(10, 10, PLAQUE_L - 20, 78);
  ctx.fillStyle = violetOmbre;
  ctx.fillRect(10, 82, PLAQUE_L - 20, 6);
  ctx.fillStyle = liseré;
  ctx.fillRect(10, 88, PLAQUE_L - 20, 6);
  ctx.fillStyle = clair;
  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "left";
  ctx.font = `56px ${POLICE_PIXEL}`;
  ctx.fillText("MeTube Studio", 44, 66);
  ctx.textAlign = "right";
  ctx.fillText(d.hardcore ? "Castelclic · hardcore" : "Castelclic", PLAQUE_L - 44, 66);

  // ── La plaque émaillée : bleu plaque en escalier, liseré crème, ombre dure ──
  const pL = 900;
  const pH = 252;
  const pX = (PLAQUE_L - pL) / 2;
  const pY = 128;
  boiteEscalier(ctx, pX + 14, pY + 14, pL, pH, 12, "rgba(21, 15, 10, 0.45)");
  boiteEscalier(ctx, pX, pY, pL, pH, 12, liseré);
  boiteEscalier(ctx, pX + 6, pY + 6, pL - 12, pH - 12, 12, bleu);
  // Le filet crème de l'émail, à 24 px du bord : quatre bandes de 6 px.
  ctx.fillStyle = clair;
  ctx.fillRect(pX + 30, pY + 30, pL - 60, 6);
  ctx.fillRect(pX + 30, pY + pH - 36, pL - 60, 6);
  ctx.fillRect(pX + 30, pY + 30, 6, pH - 60);
  ctx.fillRect(pX + pL - 36, pY + 30, 6, pH - 60);

  ctx.fillStyle = clair;
  ctx.font = `48px ${POLICE_PIXEL}`;
  texteEspace(ctx, "R U E   D E", PLAQUE_L / 2, pY + 92, 6);

  ctx.textAlign = "center";
  const tailleNom = taillePourTenir(ctx, d.nomChaine, POLICE_PIXEL, 132, pL - 130, 48);
  ctx.font = `${tailleNom}px ${POLICE_PIXEL}`;
  ctx.fillText(d.nomChaine, PLAQUE_L / 2, pY + 208);

  // ── Ce que la chaîne a été ──
  ctx.textAlign = "center";
  ctx.fillStyle = liseré;
  ctx.font = `76px ${POLICE_PIXEL}`;
  ctx.fillText(`${nombreFr(d.abonnes)} abonnés`, PLAQUE_L / 2, 454);

  ctx.fillStyle = crayon;
  ctx.font = `40px ${POLICE_PIXEL}`;
  ctx.fillText(
    `jour ${nombreFr(d.jours)} · ${nombreFr(d.videos)} vidéo${d.videos > 1 ? "s" : ""}`,
    PLAQUE_L / 2,
    496,
  );

  // ── Le bandeau du bas : uniquement ce qu'on SAIT ──
  const lignes: string[] = [];
  if (d.rang !== null) lignes.push(`#${nombreFr(d.rang)} au classement`);
  if (d.score !== null) lignes.push(`${nombreFr(d.score)} / 1 000 points`);
  if (d.finTitre) lignes.push(d.finTitre);
  if (d.legende) lignes.push("morte en légende");

  if (lignes.length > 0) {
    const bas = lignes.join("   ·   ");
    ctx.font = `40px ${POLICE_PIXEL}`;
    const l = Math.round(ctx.measureText(bas).width + 64);
    const x = Math.round((PLAQUE_L - l) / 2);
    boiteEscalier(ctx, x, BANDEAU_Y, l, BANDEAU_H, 6, liseré);
    boiteEscalier(ctx, x + 4, BANDEAU_Y + 4, l - 8, BANDEAU_H - 8, 6, kraft);
    ctx.fillStyle = liseré;
    ctx.textAlign = "center";
    ctx.fillText(bas, PLAQUE_L / 2, BANDEAU_Y + 40);
  }

  ctx.fillStyle = crayon;
  ctx.textAlign = "center";
  ctx.font = `32px ${POLICE_PIXEL}`;
  ctx.fillText("Objectif 1 Million", PLAQUE_L / 2, PIED_Y);
}

/** Un nom de fichier sûr, tiré du nom de chaîne (accents et espaces compris). */
function nomDeFichier(nomChaine: string): string {
  const base = nomChaine
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // les diacritiques détachés par NFD
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return `plaque-${base.length > 0 ? base : "ma-chaine"}.png`;
}

export function PlaqueCanvas(props: DonneesPlaque) {
  const ref = useRef<HTMLCanvasElement>(null);
  const toast = useUIStore((s) => s.toast);
  /**
   * Le geste est-il disponible ? Testé **au montage**, pas au clic : on ne montre pas un bouton
   * pour découvrir en le pressant qu'il ne fait rien. `undefined` = pas encore su (SSR, premier
   * rendu) ; on ne rend le bouton qu'une fois la réponse connue.
   */
  const [telechargeable, setTelechargeable] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    setTelechargeable(
      typeof HTMLAnchorElement !== "undefined" &&
        "download" in HTMLAnchorElement.prototype &&
        typeof URL.createObjectURL === "function",
    );
  }, []);

  const { nomChaine, abonnes, jours, videos, score, rang, finTitre, hardcore, legende } = props;

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const donnees: DonneesPlaque = {
      nomChaine,
      abonnes,
      jours,
      videos,
      score,
      rang,
      finTitre,
      hardcore,
      legende,
    };
    dessiner(ctx, donnees);
    // ⟦C48⟧ VT323 est une police locale (`@font-face`, `app.css`) : le navigateur ne la charge que
    // lorsqu'un texte la demande, et un canvas ne déclenche pas ce chargement tout seul. On la
    // demande explicitement, puis on redessine — sans ça, la plaque s'exporte dans la police de
    // repli (le même piège que le premier rendu d'avant, pour une autre raison).
    if (typeof document !== "undefined" && document.fonts?.load) {
      const redessiner = () => {
        const c = ref.current?.getContext("2d");
        if (c) dessiner(c, donnees);
      };
      void document.fonts.load('56px "VT323"').then(redessiner, redessiner);
      void document.fonts.ready.then(redessiner);
    }
  }, [nomChaine, abonnes, jours, videos, score, rang, finTitre, hardcore, legende]);

  function telecharger() {
    const canvas = ref.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) {
        toast({
          texte: "L'image n'a pas pu être fabriquée — clic droit dessus, « Enregistrer l'image ».",
          ton: "mal",
          icone: "🖼",
        });
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nomDeFichier(nomChaine);
      // Firefox exige que le lien soit dans le document pour honorer le clic programmatique.
      document.body.appendChild(a);
      a.click();
      a.remove();
      // On libère l'URL après le tour de boucle : la révoquer tout de suite annule le
      // téléchargement sur certains navigateurs.
      window.setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast({ texte: "Plaque enregistrée. Elle est à toi.", ton: "bien", icone: "🏆" });
    }, "image/png");
  }

  return (
    <figure className="plaque-partage">
      <canvas
        ref={ref}
        width={PLAQUE_L}
        height={PLAQUE_H}
        className="plaque-partage-canvas"
        role="img"
        aria-label={`Plaque de rue au nom de ${nomChaine}, ${nombreFr(abonnes)} abonnés au jour ${jours}`}
      />
      <figcaption className="plaque-partage-pied">
        {telechargeable === true ? (
          <button type="button" className="bouton" onClick={telecharger}>
            ⬇ Télécharger la plaque
            <span className="cout">PNG {PLAQUE_L}×{PLAQUE_H} — la taille des cartes de partage</span>
          </button>
        ) : telechargeable === false ? (
          <p className="ligne-info discret">
            Ce navigateur ne laisse pas la page enregistrer un fichier. L'image est là :
            clic droit dessus, « Enregistrer l'image sous… ».
          </p>
        ) : null}
      </figcaption>
    </figure>
  );
}
